// client/src/cognitive-flows/components/canvas/manualTable.js
//
// The edit operations for a manual table block, as pure config → config
// functions. Shared by the drawer editor (ManualTableEditor) and the on-card
// editor (ManualTableGrid) so the two can never disagree about what "add a
// column" means — the card and the drawer edit the same block, often in the
// same sitting.
//
// Caps mirror the server's sanitizeManualTable (canvasStore.js). Enforcing
// them here as well is not redundancy: without it the UI lets someone build a
// 300-row table and the server silently truncates it on save.

export const MAX_COLS = 12;
export const MAX_ROWS = 200;

const cols = (cfg) => (Array.isArray(cfg?.columns) ? cfg.columns : []);
const rows = (cfg) => (Array.isArray(cfg?.rows) ? cfg.rows : []);

export const canAddRow = (cfg) => rows(cfg).length < MAX_ROWS;
export const canAddColumn = (cfg) => cols(cfg).length < MAX_COLS;

export function setCell(cfg, r, c, value) {
    const columns = cols(cfg);
    return {
        ...cfg,
        rows: rows(cfg).map((row, i) => (i === r
            // Rebuild against the column count so a row that was short stays
            // rectangular rather than growing a hole at index c.
            ? columns.map((_, j) => (j === c ? value : (row?.[j] ?? '')))
            : row)),
    };
}

export function setHeader(cfg, c, value) {
    return { ...cfg, columns: cols(cfg).map((h, i) => (i === c ? value : h)) };
}

export function addRow(cfg) {
    if (!canAddRow(cfg)) return cfg;
    const columns = cols(cfg);
    // A first row with no columns yet would be invisible — give it one.
    if (columns.length === 0) return { ...cfg, columns: ['Column 1'], rows: [['']] };
    return { ...cfg, rows: [...rows(cfg), columns.map(() => '')] };
}

export function addColumn(cfg) {
    if (!canAddColumn(cfg)) return cfg;
    const columns = cols(cfg);
    const next = [...columns, `Column ${columns.length + 1}`];
    // A brand-new table gets a row too, so there is somewhere to type.
    const body = rows(cfg).length === 0 ? [next.map(() => '')] : rows(cfg).map((r) => [...r, '']);
    return { ...cfg, columns: next, rows: body };
}

export function removeRow(cfg, r) {
    return { ...cfg, rows: rows(cfg).filter((_, i) => i !== r) };
}

export function removeColumn(cfg, c) {
    const next = cols(cfg).filter((_, i) => i !== c);
    // Dropping the last column would leave orphaned rows that every renderer
    // reads as an empty table — clear them together.
    return { ...cfg, columns: next, rows: next.length === 0 ? [] : rows(cfg).map((r) => r.filter((_, i) => i !== c)) };
}

/**
 * One delimited line → its fields. Tab-separated data is taken literally
 * (spreadsheets never quote tabs); comma-separated goes through a quote-aware
 * pass, because "Doe, John" in a CSV is one field, not two.
 *
 * A quoted field spanning multiple LINES is not supported — rare in a
 * copy-paste, and supporting it would need a full streaming parser.
 */
function splitLine(line, delimiter) {
    if (delimiter === '\t') return line.split('\t');
    const out = [];
    let cur = '';
    let quoted = false;
    for (let i = 0; i < line.length; i += 1) {
        const ch = line[i];
        if (quoted) {
            if (ch === '"' && line[i + 1] === '"') { cur += '"'; i += 1; }
            else if (ch === '"') quoted = false;
            else cur += ch;
        } else if (ch === '"') quoted = true;
        else if (ch === ',') { out.push(cur); cur = ''; }
        else cur += ch;
    }
    out.push(cur);
    return out;
}

/** Pasted spreadsheet text → `{ columns, rows }`, or null when there's nothing usable. */
export function parsePastedTable(text, { firstRowIsHeader = true } = {}) {
    const lines = String(text || '').replace(/\r\n?/g, '\n').split('\n').filter((l) => l.trim() !== '');
    if (lines.length === 0) return null;
    // Tab wins wherever present: that is what Excel and Sheets put on the
    // clipboard, and a cell's own text may legitimately contain commas.
    const delimiter = lines.some((l) => l.includes('\t')) ? '\t' : ',';
    const grid = lines.map((l) => splitLine(l, delimiter).map((c) => c.trim()));
    const width = Math.min(MAX_COLS, Math.max(...grid.map((r) => r.length)));
    if (width === 0) return null;

    const columns = firstRowIsHeader
        ? grid[0].slice(0, width).map((c, i) => c || `Column ${i + 1}`)
        : Array.from({ length: width }, (_, i) => `Column ${i + 1}`);
    const body = (firstRowIsHeader ? grid.slice(1) : grid).slice(0, MAX_ROWS);
    return { columns, rows: body.map((r) => Array.from({ length: width }, (_, i) => r[i] ?? '')) };
}
